import { Component, useMemo, useRef, useEffect } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { Environment } from "@react-three/drei";
import * as THREE from "three";
import "../styles/BloodHelixHero.css";
import fallbackBackground from "../assets/liveforever-background.webp";

// Probe for a usable WebGL context once at module load. Some environments
// (hardware accel disabled, sandboxed/headless browsers, blocklisted GPUs)
// refuse a context entirely — see the "Sandboxed = yes / GL_VENDOR = Disabled"
// failures. Mounting <Canvas> there throws synchronously and, because this hero
// backs the whole app layout, takes the entire page down. Detect up front and
// fall back to the static background instead.
function detectWebGL() {
  if (typeof document === "undefined") return false;
  try {
    const canvas = document.createElement("canvas");
    const gl =
      canvas.getContext("webgl2") ||
      canvas.getContext("webgl") ||
      canvas.getContext("experimental-webgl");
    return !!gl;
  } catch {
    return false;
  }
}

const WEBGL_AVAILABLE = detectWebGL();

// Dev aid: append ?staticbg=1 to the URL to force the static fallback even when
// WebGL works, so the fallback image can be previewed and tuned.
function forceStaticBackground() {
  if (typeof window === "undefined") return false;
  try {
    return new URLSearchParams(window.location.search).has("staticbg");
  } catch {
    return false;
  }
}

// True when the hero is showing the static image instead of the live 3D scene.
// Callers use this to drop helix-only affordances (the pause toggle, the
// wheel-driven camera parallax) that make no sense without a running scene.
export function usingStaticFallback() {
  return !WEBGL_AVAILABLE || forceStaticBackground();
}

// Static fallback rendered whenever the 3D scene can't run. Shows the hero
// background image over the solid backdrop so the page looks intentional
// rather than broken. The image covers the viewport and stays centered.
function StaticBackground() {
  return (
    <div
      aria-hidden="true"
      style={{
        width: "100%",
        height: "100%",
        background: "#05070b",
        backgroundImage: `url(${fallbackBackground})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    />
  );
}

// Catches a context-creation/-loss error that slips past the pre-flight probe
// (e.g. context created then lost, or driver failure during init) so a WebGL
// failure degrades to the static background instead of crashing the app tree.
class WebGLErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error) {
    // eslint-disable-next-line no-console
    console.warn("BloodHelixHero: WebGL unavailable, using static background.", error);
  }

  render() {
    if (this.state.failed) return <StaticBackground />;
    return this.props.children;
  }
}

// Shared camera constants — Canvas initial position must match ScrollCamera at progress=0
const CAMERA_DISTANCE = 14;
const MIN_POLAR = Math.PI / 2.2;
const MAX_POLAR = Math.PI / 1.8;

// Evans & Fung (1972) biconcave disc profile, revolved via LatheGeometry
function useRBCGeometry() {
  return useMemo(() => {
    const C0 = 0.207161;
    const C1 = 2.002558;
    const C2 = -1.122762;
    const R = 1.0;

    const segments = 64;
    const topPoints = [];
    const bottomPoints = [];

    for (let i = 0; i <= segments; i++) {
      const t = i / segments; // 0 (center) to 1 (rim)
      const r = t * R;
      const x = r / R;

      const envelope = Math.sqrt(Math.max(0, 1 - x * x));
      const poly = C0 + C1 * x * x + C2 * x * x * x * x;
      const z = (R / 2) * envelope * poly;

      topPoints.push(new THREE.Vector2(r, z));
      bottomPoints.push(new THREE.Vector2(r, -z));
    }

    // Small semicircular arc at the rim to smooth the top↔bottom seam
    const lastTop = topPoints[topPoints.length - 1];
    const lastBot = bottomPoints[bottomPoints.length - 1];
    const rimR = lastTop.x;
    const rimHalfHeight = (lastTop.y - lastBot.y) / 2;
    const rimMidY = (lastTop.y + lastBot.y) / 2;
    const edgeSteps = 8;
    const edgePoints = [];
    for (let i = 1; i < edgeSteps; i++) {
      const a = (i / edgeSteps) * Math.PI; // 0→PI arc from top to bottom
      edgePoints.push(
        new THREE.Vector2(
          rimR + Math.sin(a) * rimHalfHeight * 0.3,
          rimMidY + Math.cos(a) * rimHalfHeight
        )
      );
    }

    const fullProfile = [
      ...topPoints,
      ...edgePoints,
      ...bottomPoints.reverse(),
    ];

    const geo = new THREE.LatheGeometry(fullProfile, 32);
    geo.computeVertexNormals();
    return geo;
  }, []);
}

const BASE_COLOR = new THREE.Color("#8a0c1a");

const rbcMaterial = new THREE.MeshPhysicalMaterial({
  color: BASE_COLOR.clone(),
  roughness: 0.7,
  metalness: 0,
  transmission: 0,
  thickness: 0,
  clearcoat: 0,
  clearcoatRoughness: 0.8,
  transparent: false,
  opacity: 1,
  side: THREE.DoubleSide,
  emissive: new THREE.Color(0x000000),
  emissiveIntensity: 0,
});

const FLASH_WHITE_DUR = 0.25;
const FLASH_ORANGE_DUR = 1.0;
const FLASH_FADE_DUR = 0.6;
const FLASH_TOTAL = FLASH_WHITE_DUR + FLASH_ORANGE_DUR + FLASH_FADE_DUR;
const MAX_ACTIVE_FLASHES = 1;

function createFlashSystem() {
  return { cells: [], active: [], nextFlashAt: 0 };
}

function BloodCell({ position, rotation, scale, geometry, flashSystem }) {
  const meshRef = useRef(null);
  const material = useMemo(() => rbcMaterial.clone(), []);

  useEffect(() => {
    const sys = flashSystem?.current;
    if (!sys || !meshRef.current) return;
    const entry = { mesh: meshRef.current, material };
    sys.cells.push(entry);
    return () => {
      const idx = sys.cells.indexOf(entry);
      if (idx >= 0) sys.cells.splice(idx, 1);
      sys.active = sys.active.filter((f) => f.entry !== entry);
    };
  }, [flashSystem, material]);

  useEffect(() => () => material.dispose(), [material]);

  return (
    <mesh
      ref={meshRef}
      position={position}
      rotation={rotation}
      scale={scale}
      geometry={geometry}
      material={material}
    />
  );
}

function BloodHelix({ count = 120, radius = 4, height = 16, turns = 2, flashSystem, pausedRef }) {
  const groupRef = useRef(null);
  const geometry = useRBCGeometry();

  const cells = useMemo(() => {
    const items = [];

    // Two strands offset by PI for a double helix
    for (let strand = 0; strand < 2; strand++) {
      const offset = strand * Math.PI;

      for (let i = 0; i < count; i++) {
        const p = i / count;
        const angle = p * Math.PI * 2 * turns + offset;

        const x = Math.cos(angle) * radius;
        const y = (p - 0.5) * height;
        const z = Math.sin(angle) * radius;

        const rotX = Math.random() * 0.8 - 0.4;
        const rotY = angle + Math.PI / 2 + (Math.random() * 0.5 - 0.25);
        const rotZ = Math.random() * 0.8 - 0.4;

        const s = 0.22 + Math.random() * 0.06;

        items.push({
          position: [x, y, z],
          rotation: [rotX, rotY, rotZ],
          scale: [s, s, s],
        });
      }
    }

    return items;
  }, [count, radius, height, turns]);

  useFrame((state, delta) => {
    if (pausedRef?.current) return;
    if (!groupRef.current) return;
    groupRef.current.rotation.y += delta * 0.22;
    groupRef.current.rotation.x =
      Math.sin(state.clock.elapsedTime * 0.25) * 0.08;
  });

  return (
    <group ref={groupRef}>
      {cells.map((cell, i) => (
        <BloodCell
          key={i}
          geometry={geometry}
          position={cell.position}
          rotation={cell.rotation}
          scale={cell.scale}
          flashSystem={flashSystem}
        />
      ))}
    </group>
  );
}

// Drop-in replacement for drei's <Float> that honors pausedRef by accumulating
// its own clamped clock, so demand-mode frames fired on resize don't jump the
// float position forward based on the real-time elapsed clock.
function LocalFloat({ speed, rotationIntensity, floatIntensity, offset, pausedRef, children }) {
  const ref = useRef(null);
  const localTime = useRef(offset);

  useFrame((state, delta) => {
    if (pausedRef?.current) return;
    if (!ref.current) return;
    localTime.current += Math.min(delta, 0.05);
    // Matches drei's <Float> phase formula: (t / 4) * speed, with per-axis
    // amplitudes of 1/8, 1/8, 1/20, 1/10.
    const phase = (localTime.current / 4) * speed;
    ref.current.rotation.x = (Math.cos(phase) / 8) * rotationIntensity;
    ref.current.rotation.y = (Math.sin(phase) / 8) * rotationIntensity;
    ref.current.rotation.z = (Math.sin(phase) / 20) * rotationIntensity;
    ref.current.position.y = (Math.sin(phase) / 10) * floatIntensity;
  });

  return <group ref={ref}>{children}</group>;
}

function FloatingCells({ count = 80, flashSystem, pausedRef }) {
  const groupRef = useRef(null);
  const geometry = useRBCGeometry();
  const localTime = useRef(0);

  const cells = useMemo(() => {
    const items = [];

    for (let i = 0; i < count; i++) {
      const spread = 16;
      const x = (Math.random() - 0.5) * spread;
      const y = (Math.random() - 0.5) * spread;
      const z = (Math.random() - 0.5) * spread;

      const rotX = Math.random() * Math.PI;
      const rotY = Math.random() * Math.PI;
      const rotZ = Math.random() * Math.PI;

      const s = 0.18;

      items.push({
        position: [x, y, z],
        rotation: [rotX, rotY, rotZ],
        scale: [s, s, s],
      });
    }

    return items;
  }, [count]);

  useFrame((state, delta) => {
    if (pausedRef?.current) return;
    if (!groupRef.current) return;
    localTime.current += Math.min(delta, 0.05);
    groupRef.current.rotation.y = localTime.current * 0.03;
  });

  return (
    <group ref={groupRef}>
      {cells.map((cell, i) => (
        <LocalFloat
          key={i}
          speed={0.8 + (i % 5) * 0.15}
          rotationIntensity={0.4}
          floatIntensity={0.7}
          offset={i * 0.37}
          pausedRef={pausedRef}
        >
          <BloodCell
            geometry={geometry}
            position={cell.position}
            rotation={cell.rotation}
            scale={cell.scale}
            flashSystem={flashSystem}
          />
        </LocalFloat>
      ))}
    </group>
  );
}

function createFlareTexture() {
  const size = 128;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  const gradient = ctx.createRadialGradient(
    size / 2, size / 2, 0,
    size / 2, size / 2, size / 2
  );
  gradient.addColorStop(0, "rgba(255,255,255,1)");
  gradient.addColorStop(0.15, "rgba(255,235,200,0.85)");
  gradient.addColorStop(0.4, "rgba(255,160,90,0.35)");
  gradient.addColorStop(0.75, "rgba(255,80,30,0.08)");
  gradient.addColorStop(1, "rgba(255,60,20,0)");
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, size, size);
  const tex = new THREE.CanvasTexture(canvas);
  tex.needsUpdate = true;
  return tex;
}

function FlashManager({ flashSystem, pausedRef }) {
  const flareTexture = useMemo(createFlareTexture, []);
  const spritesRef = useRef([]);
  const timeRef = useRef(0);

  useEffect(() => () => flareTexture.dispose(), [flareTexture]);

  useFrame((state, delta) => {
    if (pausedRef?.current) return;
    // Local clock that only advances with clamped per-frame delta, so a pause
    // (frameloop="demand") freezes time cleanly and a resume can't produce a jump
    // that would leave lens-flare sprites stuck mid-animation.
    timeRef.current += Math.min(delta, 0.05);
    const now = timeRef.current;
    const sys = flashSystem.current;
    if (!sys) return;

    if (
      now >= sys.nextFlashAt &&
      sys.active.length < MAX_ACTIVE_FLASHES &&
      sys.cells.length > 0
    ) {
      const cell = sys.cells[Math.floor(Math.random() * sys.cells.length)];
      if (cell) sys.active.push({ entry: cell, start: now });
      sys.nextFlashAt = now + 0.4 + Math.random() * 1.1;
    }

    for (let i = sys.active.length - 1; i >= 0; i--) {
      const flash = sys.active[i];
      const mat = flash.entry.material;
      const t = now - flash.start;

      if (t < FLASH_WHITE_DUR) {
        mat.color.setRGB(1, 1, 1);
        mat.emissive.setRGB(1, 1, 1);
        mat.emissiveIntensity = 3.5;
      } else if (t < FLASH_WHITE_DUR + FLASH_ORANGE_DUR) {
        const k = (t - FLASH_WHITE_DUR) / FLASH_ORANGE_DUR;
        mat.color.setRGB(1, 0.42, 0.12);
        mat.emissive.setRGB(1, 0.35, 0.08);
        mat.emissiveIntensity = 1.6 * (1 - k) + 0.15;
      } else if (t < FLASH_TOTAL) {
        const k = (t - FLASH_WHITE_DUR - FLASH_ORANGE_DUR) / FLASH_FADE_DUR;
        mat.color.setRGB(
          THREE.MathUtils.lerp(1, BASE_COLOR.r, k),
          THREE.MathUtils.lerp(0.42, BASE_COLOR.g, k),
          THREE.MathUtils.lerp(0.12, BASE_COLOR.b, k)
        );
        mat.emissive.setRGB(0, 0, 0);
        mat.emissiveIntensity = 0;
      } else {
        mat.color.copy(BASE_COLOR);
        mat.emissive.setRGB(0, 0, 0);
        mat.emissiveIntensity = 0;
        sys.active.splice(i, 1);
      }
    }

    for (let i = 0; i < MAX_ACTIVE_FLASHES; i++) {
      const sprite = spritesRef.current[i];
      if (!sprite) continue;
      const flash = sys.active[i];
      if (!flash || !flash.entry.mesh) {
        sprite.visible = false;
        continue;
      }
      const t = now - flash.start;
      const FLARE_TAIL = 0.35;
      let opacity = 0;
      let scale = 2;
      if (t < FLASH_WHITE_DUR) {
        const k = t / FLASH_WHITE_DUR;
        opacity = 1 - k * 0.15;
        scale = 2 + (1 - k) * 1.4;
      } else if (t < FLASH_WHITE_DUR + FLARE_TAIL) {
        const k = (t - FLASH_WHITE_DUR) / FLARE_TAIL;
        const ease = (1 - k) * (1 - k);
        opacity = 0.5 * ease;
        scale = 2 - k * 0.9;
      } else {
        sprite.visible = false;
        continue;
      }
      sprite.visible = opacity > 0.01;
      flash.entry.mesh.getWorldPosition(sprite.position);
      sprite.scale.set(scale, scale, scale);
      sprite.material.opacity = opacity;
    }
  });

  return (
    <>
      {Array.from({ length: MAX_ACTIVE_FLASHES }).map((_, i) => (
        <sprite
          key={i}
          ref={(el) => (spritesRef.current[i] = el)}
          visible={false}
        >
          <spriteMaterial
            map={flareTexture}
            transparent
            depthWrite={false}
            blending={THREE.AdditiveBlending}
          />
        </sprite>
      ))}
    </>
  );
}

function GlowCore() {
  return (
    <mesh>
      <sphereGeometry args={[1.2, 32, 32]} />
      <meshBasicMaterial color="#5c0a14" transparent opacity={0.1} />
    </mesh>
  );
}

// Drives camera polar angle from scroll progress, keeps azimuth fixed
function ScrollCamera({ scrollProgress, pausedRef }) {
  const { camera } = useThree();
  const smoothed = useRef(0);

  useFrame((state, delta) => {
    if (pausedRef?.current) return;
    const target = scrollProgress.current;
    const k = 1 - Math.exp(-Math.min(delta, 0.1) * 7);
    smoothed.current += (target - smoothed.current) * k;
    const polar = MIN_POLAR + smoothed.current * (MAX_POLAR - MIN_POLAR);
    camera.position.set(0, CAMERA_DISTANCE * Math.cos(polar), CAMERA_DISTANCE * Math.sin(polar));
    camera.lookAt(0, 0, 0);
  });

  return null;
}

// Horizontal-only drag rotates the scene group; vertical touches pass through to browser scroll.
// Listens on window because the Canvas sits in a fixed z:0 div beneath the page content,
// so pointerdown on the canvas element itself never fires — events hit the foreground layer.
function DragRotate({ sceneRef, pausedRef, scrollProgress }) {
  const dragging = useRef(false);
  const lastX = useRef(0);
  const velocity = useRef(0);

  useEffect(() => {
    const isInteractive = (el) => {
      if (!el || !(el instanceof Element)) return false;
      return !!el.closest('button, a, input, textarea, select, [role="button"]');
    };

    const onPointerDown = (e) => {
      // Only engage drag-to-rotate on the landing page, and only while the
      // page is at the very top (no scroll, no parallax progress).
      if (window.location.pathname !== "/") return;
      if (pausedRef?.current) return;
      if (window.scrollY > 1) return;
      if (scrollProgress?.current > 0.1) return;
      if (isInteractive(e.target)) return;
      dragging.current = true;
      lastX.current = e.clientX;
      velocity.current = 0;
    };

    const onPointerMove = (e) => {
      if (!dragging.current || !sceneRef.current) return;
      const dx = e.clientX - lastX.current;
      sceneRef.current.rotation.y += dx * 0.005;
      velocity.current = dx * 0.005;
      lastX.current = e.clientX;
    };

    const onPointerUp = () => {
      dragging.current = false;
    };

    window.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);

    return () => {
      window.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
    };
  }, [sceneRef, pausedRef, scrollProgress]);

  // Inertia: keep spinning after release. Zero velocity while paused
  // so residual spin doesn't jitter on resume.
  useFrame(() => {
    if (pausedRef?.current) { velocity.current = 0; return; }
    if (!dragging.current && sceneRef.current && Math.abs(velocity.current) > 0.0001) {
      sceneRef.current.rotation.y += velocity.current;
      velocity.current *= 0.95;
    }
  });

  return null;
}

export default function BloodHelixHero({ paused = false, scrollProgressRef }) {
  const sceneRef = useRef(null);
  const flashSystemRef = useRef(createFlashSystem());
  const internalScrollProgress = useRef(0);
  const scrollProgress = scrollProgressRef ?? internalScrollProgress;
  const pausedRef = useRef(paused);
  useEffect(() => {
    pausedRef.current = paused;
  }, [paused]);

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        background: "#05070b",
      }}
    >
      {usingStaticFallback() ? (
        <StaticBackground />
      ) : (
      <WebGLErrorBoundary>
      <Canvas
        camera={{ position: [0, CAMERA_DISTANCE * Math.cos(MIN_POLAR), CAMERA_DISTANCE * Math.sin(MIN_POLAR)], fov: 45 }}
        style={{ touchAction: "pan-y" }}
        frameloop={paused ? "demand" : "always"}
      >
        <fog attach="fog" args={["#05070b", 10, 26]} />

        <ambientLight intensity={1.2} />
        <directionalLight position={[5, 5, 5]} intensity={0.8} />
        <directionalLight position={[-5, -3, -5]} intensity={0.4} />
        <pointLight position={[-6, 2, 4]} intensity={3} color="#cc2233" />
        <pointLight position={[6, -2, -4]} intensity={2} color="#cc2233" />
        <pointLight position={[0, 5, 0]} intensity={1.5} color="#ffb3b8" />

        <Environment preset="studio" environmentIntensity={0.3} />

        <ScrollCamera scrollProgress={scrollProgress} pausedRef={pausedRef} />
        <DragRotate sceneRef={sceneRef} pausedRef={pausedRef} scrollProgress={scrollProgress} />

        <group ref={sceneRef}>
          <GlowCore />
          <BloodHelix flashSystem={flashSystemRef} pausedRef={pausedRef} />
          <FloatingCells flashSystem={flashSystemRef} pausedRef={pausedRef} />
        </group>
        <FlashManager flashSystem={flashSystemRef} pausedRef={pausedRef} />
      </Canvas>
      </WebGLErrorBoundary>
      )}
    </div>
  );
}
